import React, { useState, useEffect, useRef } from "react";

// seo
import Seo from "../components/Seo";

// services
import { Mixpanel, MixpanelEvents } from "../services/Mixpanel";
import { sendToLSQ } from "../services/LeadSquared";

// utils
import { getSessionId } from "../utils";

// constants
import constant from "../constants";

// hooks
import { useWindowSize } from "../hooks/useWindowSize";
import { useCommonPageTracking } from "../hooks/usePageTracking";

// components
import Banner from "../components/Banner";
import AiDigitalTwin from "../components/Home/AiDigitalTwin";
import ConditionsReverse from "../components/Home/ConditionsReverse";
import HowTwinWorkComponent from "../components/Home/HowTwinWork/HowTwinWork";
import HealthyMetabolismComponent from "../components/Home/HealthyMetabolism/HealthyMetabolism";
import ClinicalResearchComponent from "../components/Home/ClinicalResearch/ClinicalResearch";
import FaqComponent from "../components/Home/Faq/Faq";
import SuccessStoriesV3 from "../components/Home/SuccessStories/SuccessStoriesV3";
import Featured from "../components/Home/Featured/Featured";
import StartJourneyComponent from "../components/Home/StartJourney/StartJourney";
import WhatTwinDo from "../components/Home/WhatTwinDo/WhatTwinDo";
import PopUpModal from "../components/Global/PopUpModal/PopUpModal";
import GetInTouchNew from "../components/Home/GetInTouchNew";

// css
import * as styles from "./index.module.css";

const IndexPage = props => {
  const [sessionId, setSessionId] = useState(getSessionId());
  const [showGetInTouchModal, setShowGetInTouchModal] = useState(false);
  const [getInTouchLoading, setGetInTouchLoading] = useState(false);
  const getInTouchShownRef = useRef(false);
  const windowSize = useWindowSize();

  // Use common page tracking hooks
  const { deviceType, operatingSystem } = useCommonPageTracking(
    "Homepage",
    props.location.search
  );

  const updateSessionId = () => {
    setSessionId(getSessionId());
  };

  // Get In Touch Popup - Show after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!getInTouchShownRef.current) {
        getInTouchShownRef.current = true;
        setShowGetInTouchModal(true);

        // Mixpanel Event
        Mixpanel.track(MixpanelEvents.pop_up_shown, {
          page_name: "Homepage",
          asset: "India Website",
          asset_name: "India Corporate Website",
          popup_name: "Get In Touch Form",
          trigger: "10 Second Timer",
          device_type: deviceType,
          operating_system: operatingSystem,
        });
      }
    }, 10000); // 10 seconds

    return () => clearTimeout(timer);
  }, [deviceType, operatingSystem]);

  // Handle Get In Touch Modal Close
  const handleGetInTouchClose = () => {
    setShowGetInTouchModal(false);
  };

  // Handle Get In Touch Form Submission
  const handleGetInTouchFormSubmit = async formData => {
    setGetInTouchLoading(true);

    // leadSquared params
    const leadSquaredParams = {
      FirstName: formData.fullName,
      Phone: formData.mobileNumber,
      mx_Website_CTA: "India Webiste Homepage Popup",
      mx_Latest_Source: "New Website",
      mx_Device_Mobile_Desktop: deviceType,
      mx_msc_Operating_System: operatingSystem,
    };
    await sendToLSQ(leadSquaredParams);
    setGetInTouchLoading(false);
    setShowGetInTouchModal(false);
  };

  // Mixpanel Events -> Cta Click events
  const handleMixpanelCTA = (ctaText, ctaLocation) => {
    Mixpanel.track(MixpanelEvents.contact_button_clicked, {
      page_name: "Homepage",
      asset: "India Website",
      asset_name: "India Corporate Website",
      cta_text: ctaText,
      cta_location: ctaLocation,
      device_type: deviceType,
      operating_system: operatingSystem,
    });
  };

  // Mixpanel Events -> FAQ Click Events
  const handleMixpanelCTAFaq = (faqText, faqDesc) => {
    Mixpanel.track(MixpanelEvents.faq_clicked, {
      page_name: "HomePage",
      asset: "India Website",
      asset_name: "India Corporate Website",
      faq_text: faqText,
      faq_desc: faqDesc,
      faq_location: "HomePage - FAQ Clicked",
      device_type: deviceType,
      operating_system: operatingSystem,
    });
  };

  return (
    <>
      <Seo
        title="Twin Health India: The Power of a Healthy Metabolism"
        description="Twin Health helps you reverse diabetes, PMOS and more with your own AI Digital Twin, so you can balance blood sugar, cut medicines and feel better."
      />

      <Banner
        title="AI-powered Digital Twin™ for Metabolic Health"
        description="Experience metabolic health transformation with AI Digital Twin™ technology. This program goes beyond temporary fixes to heal metabolism at its root, helping you improve metabolism, achieve sustainable diabetes remission, and naturally support PMOS improvement."
        ctaText="Start Your Metabolic Journey"
        ctaLink="/contact/"
        onCtaClick={() =>
          handleMixpanelCTA(
            "Start Your Metabolic Journey",
            "Homepage - Banner Section"
          )
        }
      />

      <AiDigitalTwin
        cardsData={constant.AI_DIGITAL_TWIN_CARDS}
        onCtaClick={() =>
          handleMixpanelCTA("Learn More", "Homepage - AI Digital Twin Section")
        }
      />

      <ConditionsReverse
        onCtaClick={(e, condition) =>
          handleMixpanelCTA(
            e ? "Start your journey today" : "arrow cta clicked",
            `Homepage - Conditions Reverse Section - ${condition}`
          )
        }
      />

      <HowTwinWorkComponent
        cardsData={constant.HOW_TWIN_WORK_CARDS}
        onArrowClick={action =>
          handleMixpanelCTA(
            action,
            "Homepage - How Twin Work Section - Arrow Button"
          )
        }
      />

      <HealthyMetabolismComponent />

      <WhatTwinDo
        onCtaClick={cardName =>
          handleMixpanelCTA(cardName, "Homepage - What Twin Do Section")
        }
      />

      <ClinicalResearchComponent
        carouselData={constant.CLINICAL_RESEARCH_CARDS}
      />

      <Featured />

      <SuccessStoriesV3
        title="Hear From Our Members"
        subTitle="Members across India have improved metabolism, achieved
                  diabetes remission, reduced PMOS symptoms and restored energy
                  through sustainable metabolic changes."
        videoData={constant.SUCCESS_STORIES_VIDEOS}
        handleMixpanelCTA={handleMixpanelCTA}
        sessionId={sessionId}
        updateSessionId={updateSessionId}
        video_name="Homepage - Success Stories Video"
      />

      <StartJourneyComponent
        onCtaClick={() =>
          handleMixpanelCTA(
            "Start Your Metabolic Plan",
            "Homepage - Start Health Journey Section"
          )
        }
      />

      <FaqComponent
        title="Frequently Asked Questions"
        accordionPayload={constant.HOMEPAGE_FAQ}
        onCtaClick={(faqText, faqDesc) =>
          handleMixpanelCTAFaq(faqText, faqDesc)
        }
      />

      {/* Get In Touch Popup Modal */}
      <PopUpModal
        show={showGetInTouchModal}
        onHide={handleGetInTouchClose}
        size="xl"
        centered={true}
        backdrop="static"
        keyboard={true}
        showHeader={true}
        showCloseButton={true}
        customModalClass={styles.getInTouchModal}
        customBodyClass={styles.getInTouchModalBody}
        customHeaderClass={styles.getInTouchModalHeader}
        windowSize={windowSize}
        showCanvasModal={showGetInTouchModal}
        onHideCanvasModal={handleGetInTouchClose}
        canvasPlacement="bottom"
        canvasHeight="auto"
        pageName="Homepage - Get In Touch Popup"
      >
        <GetInTouchNew
          onFormSubmit={handleGetInTouchFormSubmit}
          mixpanelPageName="Homepage - Get In Touch Popup"
          loading={getInTouchLoading}
        />
      </PopUpModal>
    </>
  );
};

export default IndexPage;
